import React, { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import "./DailyPaymentTransaction.css";

import {
  Table,
  Card,
  Input,
  AutoComplete,
  Button,
  Tag,
  Typography,
  Spin,
  Empty,
  Row,
  Col,
  Divider,
} from "antd";

import {
  SearchOutlined,
  ReloadOutlined,
  FilePdfOutlined,
  UserOutlined,
} from "@ant-design/icons";

import { pdf } from "@react-pdf/renderer";
import api from "../../api/api";
import DailyPaymentTransactionPDF from "./DailyPaymentTransactionPDF";
import { useAcademicYear } from "../../AcademicYearContext";

const { Title, Text } = Typography;

const DailyPaymentTransactionUpdate = () => {
  const { selectedYear } = useAcademicYear();

  const [transactions, setTransactions] = useState([]);
  const [studentTransactions, setStudentTransactions] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSearch, setStudentSearch] = useState("");
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  const [totalRecords, setTotalRecords] = useState(0);

  /*
  |--------------------------------------------------------------------------
  | Load daily payment transactions
  |--------------------------------------------------------------------------
  */

  const fetchTransactions = useCallback(async () => {
    if (!selectedYear?.id) {
      setTransactions([]);
      setTotalRecords(0);
      return;
    }

    try {
      setLoading(true);

      const response = await api.get("/daily-payment-transactions/", {
        params: {
          academic_year: selectedYear.id,
          daily: true,
          page: pagination.current,
          page_size: pagination.pageSize,
          search: searchText,
        },
      });

      const result = response.data || {};
      const dailyResults = Array.isArray(result.results)
        ? result.results
        : [];

      setTransactions(dailyResults);
      setTotalRecords(Number(result.count) || 0);
    } catch (error) {
      console.error("Daily transaction loading error:", error);
      setTransactions([]);
      setTotalRecords(0);

      Swal.fire({
        icon: "error",
        title: "Unable to Load Transactions",
        text:
          error?.response?.data?.detail ||
          "The daily payment transactions could not be loaded.",
        confirmButtonColor: "#f97316",
      });
    } finally {
      setLoading(false);
    }
  }, [
    selectedYear?.id,
    pagination.current,
    pagination.pageSize,
    searchText,
  ]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  /*
  |--------------------------------------------------------------------------
  | Search students for payment history
  |--------------------------------------------------------------------------
  */

  const fetchPaidStudents = useCallback(
    async (value) => {
      setStudentSearch(value);

      if (!value?.trim() || !selectedYear?.id) {
        setStudents([]);
        return;
      }

      try {
        const response = await api.get("/daily-payment-transactions/", {
          params: {
            academic_year: selectedYear.id,
            search: value.trim(),
            page: 1,
            page_size: 100,
          },
        });

        const results = Array.isArray(response.data?.results)
          ? response.data.results
          : [];

        const uniqueStudents = Array.from(
          new Map(
            results
              .filter((item) => item.student_id != null)
              .map((item) => [
                item.student_id,
                {
                  id: item.student_id,
                  full_name: item.student_name || "Unnamed Student",
                },
              ])
          ).values()
        );

        setStudents(uniqueStudents);
      } catch (error) {
        console.error("Student search error:", error);
        setStudents([]);
      }
    },
    [selectedYear?.id]
  );

  const fetchStudentTransactions = useCallback(
    async (studentId) => {
      if (!studentId || !selectedYear?.id) {
        setStudentTransactions([]);
        return;
      }

      try {
        setLoading(true);

        const response = await api.get("/daily-payment-transactions/", {
          params: {
            academic_year: selectedYear.id,
            student: studentId,
            page: 1,
            page_size: 100,
          },
        });

        setStudentTransactions(
          Array.isArray(response.data?.results)
            ? response.data.results
            : []
        );
      } catch (error) {
        console.error("Student transaction loading error:", error);
        setStudentTransactions([]);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Student History",
          text: "The selected student's payment history could not be loaded.",
          confirmButtonColor: "#f97316",
        });
      } finally {
        setLoading(false);
      }
    },
    [selectedYear?.id]
  );

  const handleStudentSelect = (value, option) => {
    const student = students.find(
      (item) => String(item.id) === String(option?.key)
    );

    if (!student) return;

    setSelectedStudent(student);
    setStudentSearch(student.full_name);
    setStudents([]);
    fetchStudentTransactions(student.id);
  };

  const clearStudentSelection = () => {
    setSelectedStudent(null);
    setStudentSearch("");
    setStudentTransactions([]);
    setStudents([]);
  };

  /*
  |--------------------------------------------------------------------------
  | Table pagination and search
  |--------------------------------------------------------------------------
  */

  const handleTableChange = (tablePagination) => {
    setPagination({
      current: tablePagination.current || 1,
      pageSize: tablePagination.pageSize || 20,
    });
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination((previous) => ({
      ...previous,
      current: 1,
    }));
  };

  /*
  |--------------------------------------------------------------------------
  | Download PDF without PDFDownloadLink
  |--------------------------------------------------------------------------
  */

  const handleDownloadPDF = async () => {
    if (pdfLoading) return;

    if (!Array.isArray(transactions) || transactions.length === 0) {
      await Swal.fire({
        icon: "info",
        title: "No Transactions",
        text: "There are no payment transactions available to export.",
        confirmButtonColor: "#f97316",
      });
      return;
    }

    try {
      setPdfLoading(true);

      const document = (
        <DailyPaymentTransactionPDF
          transactions={transactions}
          title="Daily Payment Transaction Report"
          studentReport={false}
        />
      );

      const blob = await pdf(document).toBlob();

      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("The PDF generator returned an empty file.");
      }

      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");

      link.href = url;
      link.download = "Zion_Praise_Academy_Daily_Payment_Record.pdf";
      link.style.display = "none";

      window.document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1500);

      Swal.fire({
        icon: "success",
        title: "PDF Ready",
        text: "The daily payment report has been downloaded.",
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Payment PDF generation error:", error);

      await Swal.fire({
        icon: "error",
        title: "PDF Generation Failed",
        text:
          error?.message ||
          "The payment report could not be generated. Please check the PDF component and try again.",
        confirmButtonColor: "#f97316",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  /*
  |--------------------------------------------------------------------------
  | Table columns
  |--------------------------------------------------------------------------
  */

  const columns = [
    {
      title: "No.",
      key: "row_number",
      width: 70,
      fixed: "left",
      render: (_, __, index) =>
        (pagination.current - 1) * pagination.pageSize + index + 1,
    },
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 90,
      fixed: "left",
    },
    {
      title: "Student",
      dataIndex: "student_name",
      key: "student_name",
      width: 200,
      render: (value) => <Text strong>{value || "-"}</Text>,
    },
    {
      title: "Fee Type",
      dataIndex: "fee_type",
      key: "fee_type",
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Category",
      dataIndex: "payment_category",
      key: "payment_category",
      width: 150,
      render: (value) => (
        <Tag
          style={{
            background: value === "School Fee" ? "#84cc16" : "#f97316",
            color: "#111827",
            border: "none",
            fontWeight: 700,
            borderRadius: 20,
          }}
        >
          {value || "Uncategorized"}
        </Tag>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount_paid",
      key: "amount_paid",
      width: 140,
      render: (value) => (
        <Text strong style={{ color: "#f97316" }}>
          ${Number(value || 0).toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </Text>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      width: 140,
      render: (value) =>
        `$${Number(value || 0).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
    },
    {
      title: "Payer",
      dataIndex: "payer_name",
      key: "payer_name",
      width: 180,
      render: (value) => value || "-",
    },
    {
      title: "Cashier",
      dataIndex: "created_by",
      key: "created_by",
      width: 160,
      render: (value) => value || "-",
    },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      width: 150,
      render: (value) => {
        if (!value) return "-";
        const date = new Date(value);
        return Number.isNaN(date.getTime())
          ? String(value)
          : date.toLocaleDateString();
      },
    },
    {
      title: "Year",
      dataIndex: "academic_year",
      key: "academic_year",
      width: 150,
      render: (value) =>
        typeof value === "object"
          ? value?.name || value?.year || "-"
          : value || "-",
    },
  ];

  /*
  |--------------------------------------------------------------------------
  | Mobile transaction card
  |--------------------------------------------------------------------------
  */

  const MobileTransactionCard = ({ item }) => (
    <Card className="mobile-payment-card" key={item.id}>
      <Row gutter={[8, 8]}>
        <Col span={24}>
          <Text strong>{item.student_name || "Unnamed Student"}</Text>
        </Col>

        <Col span={12}>
          <Text type="secondary">Fee</Text>
          <br />
          {item.fee_type || "-"}
        </Col>

        <Col span={12}>
          <Text type="secondary">Amount</Text>
          <br />
          <Text strong style={{ color: "#f97316" }}>
            ${Number(item.amount_paid || 0).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </Text>
        </Col>

        <Col span={12}>
          <Text type="secondary">Category</Text>
          <br />
          <Tag>{item.payment_category || "Uncategorized"}</Tag>
        </Col>

        <Col span={12}>
          <Text type="secondary">Date</Text>
          <br />
          {item.created_at
            ? new Date(item.created_at).toLocaleDateString()
            : "-"}
        </Col>

        <Col span={24}>
          <Text type="secondary">Cashier</Text>
          <br />
          {item.created_by || "-"}
        </Col>
      </Row>
    </Card>
  );

  /*
  |--------------------------------------------------------------------------
  | Render page
  |--------------------------------------------------------------------------
  */

  return (
    <div className="daily-payment-wrapper">
      <Card className="daily-payment-card" bordered={false}>
        <Row
          justify="space-between"
          align="middle"
          gutter={[16, 16]}
        >
          <Col xs={24} md={16}>
            <Title
              level={3}
              style={{ margin: 0, color: "#ffffff" }}
            >
              Daily Payment Transactions
            </Title>

            <Text style={{ color: "#d1d5db" }}>
              Student payment history and daily financial records
            </Text>
          </Col>

          <Col xs={24} md={8}>
            <Tag className="active-year-tag">
              Active Year: {selectedYear?.name || "No Year"}
            </Tag>
          </Col>
        </Row>

        <Divider className="dark-divider" />

        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Input
              size="large"
              placeholder="Search payment..."
              prefix={<SearchOutlined />}
              value={searchText}
              allowClear
              onChange={(event) => handleSearch(event.target.value)}
            />
          </Col>

          <Col xs={24} md={5}>
            <Button
              block
              size="large"
              icon={<ReloadOutlined />}
              onClick={fetchTransactions}
              loading={loading}
              className="refresh-payment-btn"
            >
              Refresh
            </Button>
          </Col>

          <Col xs={24} md={5}>
            <Button
              block
              size="large"
              icon={<FilePdfOutlined />}
              onClick={handleDownloadPDF}
              loading={pdfLoading}
              disabled={transactions.length === 0 || loading}
              className="daily-pdf-btn"
            >
              {pdfLoading ? "Generating PDF..." : "Daily PDF"}
            </Button>
          </Col>
        </Row>

        <Divider className="dark-divider" />

        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={12}>
            <AutoComplete
              style={{ width: "100%" }}
              value={studentSearch}
              options={students.map((student) => ({
                key: String(student.id),
                value: student.full_name,
              }))}
              onSearch={fetchPaidStudents}
              onSelect={handleStudentSelect}
              placeholder="Search student payment history..."
              allowClear
              onClear={clearStudentSelection}
            >
              <Input
                size="large"
                prefix={<UserOutlined />}
                placeholder="Search student payment history..."
              />
            </AutoComplete>
          </Col>

          {selectedStudent && (
            <Col xs={24} md={12}>
              <Tag color="blue">
                Selected student: {selectedStudent.full_name}
              </Tag>

              <Button
                type="link"
                onClick={clearStudentSelection}
              >
                Clear selection
              </Button>
            </Col>
          )}
        </Row>

        {selectedStudent && (
          <>
            <Divider className="dark-divider" />

            <Title level={5} style={{ color: "#ffffff" }}>
              Payment History — {selectedStudent.full_name}
            </Title>

            {studentTransactions.length === 0 ? (
              <Empty description="No payment history found for this student." />
            ) : (
              <Table
                rowKey={(item) => item.id}
                columns={columns.filter(
                  (column) => column.key !== "year"
                )}
                dataSource={studentTransactions}
                pagination={{ pageSize: 10, showSizeChanger: true }}
                scroll={{ x: "max-content" }}
                tableLayout="auto"
              />
            )}
          </>
        )}

        <Divider className="dark-divider" />

        {loading ? (
          <div className="payment-loading">
            <Spin size="large" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="payment-empty">
            <Empty description="No payment records found" />
          </div>
        ) : (
          <>
            <div className="desktop-payment-table">
              <Table
                rowKey={(item) => item.id}
                columns={columns}
                dataSource={transactions}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: totalRecords,
                  showSizeChanger: true,
                  pageSizeOptions: [20, 50, 100],
                  showTotal: (total) => `Total ${total} transactions`,
                }}
                onChange={handleTableChange}
                tableLayout="auto"
                scroll={{ x: "max-content" }}
              />
            </div>

            <div className="mobile-payment-list">
              {transactions.map((item) => (
                <MobileTransactionCard
                  key={item.id}
                  item={item}
                />
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default DailyPaymentTransactionUpdate;







// import React, { useState, useEffect, useCallback } from "react";
// import Swal from "sweetalert2";
// import "./DailyPaymentTransaction.css";

// import {
//   Table,
//   Card,
//   Input,
//   AutoComplete,
//   Button,
//   Tag,
//   Typography,
//   Spin,
//   Empty,
//   Row,
//   Col,
//   Divider,
// } from "antd";

// import {
//   SearchOutlined,
//   ReloadOutlined,
//   FilePdfOutlined,
//   UserOutlined,
// } from "@ant-design/icons";

// import { pdf } from "@react-pdf/renderer";
// import api from "../../api/api";
// import DailyPaymentTransactionPDF from "./DailyPaymentTransactionPDF";
// import { useAcademicYear } from "../../AcademicYearContext";

// const { Title, Text } = Typography;

// const DailyPaymentTransactionUpdate = () => {
//   const { selectedYear } = useAcademicYear();

//   const [transactions, setTransactions] = useState([]);
//   const [studentTransactions, setStudentTransactions] = useState([]);
//   const [students, setStudents] = useState([]);
//   const [selectedStudent, setSelectedStudent] = useState(null);
//   const [studentSearch, setStudentSearch] = useState("");
//   const [searchText, setSearchText] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [pdfLoading, setPdfLoading] = useState(false);

//   const [pagination, setPagination] = useState({
//     current: 1,
//     pageSize: 20,
//   });

//   const [totalRecords, setTotalRecords] = useState(0);

//   /*
//   |--------------------------------------------------------------------------
//   | Load daily payment transactions
//   |--------------------------------------------------------------------------
//   */

//   const fetchTransactions = useCallback(async () => {
//     if (!selectedYear?.id) {
//       setTransactions([]);
//       setTotalRecords(0);
//       return;
//     }

//     try {
//       setLoading(true);

//       const response = await api.get("/daily-payment-transactions/", {
//         params: {
//           academic_year: selectedYear.id,
//           daily: true,
//           page: pagination.current,
//           page_size: pagination.pageSize,
//           search: searchText,
//         },
//       });

//       const result = response.data || {};
//       const dailyResults = Array.isArray(result.results)
//         ? result.results
//         : [];

//       setTransactions(dailyResults);
//       setTotalRecords(Number(result.count) || 0);
//     } catch (error) {
//       console.error("Daily transaction loading error:", error);
//       setTransactions([]);
//       setTotalRecords(0);

//       Swal.fire({
//         icon: "error",
//         title: "Unable to Load Transactions",
//         text:
//           error?.response?.data?.detail ||
//           "The daily payment transactions could not be loaded.",
//         confirmButtonColor: "#f97316",
//       });
//     } finally {
//       setLoading(false);
//     }
//   }, [
//     selectedYear?.id,
//     pagination.current,
//     pagination.pageSize,
//     searchText,
//   ]);

//   useEffect(() => {
//     fetchTransactions();
//   }, [fetchTransactions]);

//   /*
//   |--------------------------------------------------------------------------
//   | Search students for payment history
//   |--------------------------------------------------------------------------
//   */

//   const fetchPaidStudents = useCallback(
//     async (value) => {
//       setStudentSearch(value);

//       if (!value?.trim() || !selectedYear?.id) {
//         setStudents([]);
//         return;
//       }

//       try {
//         const response = await api.get("/daily-payment-transactions/", {
//           params: {
//             academic_year: selectedYear.id,
//             search: value.trim(),
//             page: 1,
//             page_size: 100,
//           },
//         });

//         const results = Array.isArray(response.data?.results)
//           ? response.data.results
//           : [];

//         const uniqueStudents = Array.from(
//           new Map(
//             results
//               .filter((item) => item.student_id != null)
//               .map((item) => [
//                 item.student_id,
//                 {
//                   id: item.student_id,
//                   full_name: item.student_name || "Unnamed Student",
//                 },
//               ])
//           ).values()
//         );

//         setStudents(uniqueStudents);
//       } catch (error) {
//         console.error("Student search error:", error);
//         setStudents([]);
//       }
//     },
//     [selectedYear?.id]
//   );

//   const fetchStudentTransactions = useCallback(
//     async (studentId) => {
//       if (!studentId || !selectedYear?.id) {
//         setStudentTransactions([]);
//         return;
//       }

//       try {
//         setLoading(true);

//         const response = await api.get("/daily-payment-transactions/", {
//           params: {
//             academic_year: selectedYear.id,
//             student: studentId,
//             page: 1,
//             page_size: 100,
//           },
//         });

//         setStudentTransactions(
//           Array.isArray(response.data?.results)
//             ? response.data.results
//             : []
//         );
//       } catch (error) {
//         console.error("Student transaction loading error:", error);
//         setStudentTransactions([]);

//         Swal.fire({
//           icon: "error",
//           title: "Unable to Load Student History",
//           text: "The selected student's payment history could not be loaded.",
//           confirmButtonColor: "#f97316",
//         });
//       } finally {
//         setLoading(false);
//       }
//     },
//     [selectedYear?.id]
//   );

//   const handleStudentSelect = (value, option) => {
//     const student = students.find(
//       (item) => String(item.id) === String(option?.key)
//     );

//     if (!student) return;

//     setSelectedStudent(student);
//     setStudentSearch(student.full_name);
//     setStudents([]);
//     fetchStudentTransactions(student.id);
//   };

//   const clearStudentSelection = () => {
//     setSelectedStudent(null);
//     setStudentSearch("");
//     setStudentTransactions([]);
//     setStudents([]);
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | Table pagination and search
//   |--------------------------------------------------------------------------
//   */

//   const handleTableChange = (tablePagination) => {
//     setPagination({
//       current: tablePagination.current || 1,
//       pageSize: tablePagination.pageSize || 20,
//     });
//   };

//   const handleSearch = (value) => {
//     setSearchText(value);
//     setPagination((previous) => ({
//       ...previous,
//       current: 1,
//     }));
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | Download PDF without PDFDownloadLink
//   |--------------------------------------------------------------------------
//   */

//   const handleDownloadPDF = async () => {
//     if (pdfLoading) return;

//     if (!Array.isArray(transactions) || transactions.length === 0) {
//       await Swal.fire({
//         icon: "info",
//         title: "No Transactions",
//         text: "There are no payment transactions available to export.",
//         confirmButtonColor: "#f97316",
//       });
//       return;
//     }

//     try {
//       setPdfLoading(true);

//       const document = (
//         <DailyPaymentTransactionPDF
//           transactions={transactions}
//           title="Daily Payment Transaction Report"
//           studentReport={false}
//         />
//       );

//       const blob = await pdf(document).toBlob();

//       if (!(blob instanceof Blob) || blob.size === 0) {
//         throw new Error("The PDF generator returned an empty file.");
//       }

//       const url = window.URL.createObjectURL(blob);
//       const link = window.document.createElement("a");

//       link.href = url;
//       link.download = "Zion_Praise_Academy_Daily_Payment_Record.pdf";
//       link.style.display = "none";

//       window.document.body.appendChild(link);
//       link.click();
//       link.remove();

//       window.setTimeout(() => {
//         window.URL.revokeObjectURL(url);
//       }, 1500);

//       Swal.fire({
//         icon: "success",
//         title: "PDF Ready",
//         text: "The daily payment report has been downloaded.",
//         timer: 1800,
//         showConfirmButton: false,
//       });
//     } catch (error) {
//       console.error("Payment PDF generation error:", error);

//       await Swal.fire({
//         icon: "error",
//         title: "PDF Generation Failed",
//         text:
//           error?.message ||
//           "The payment report could not be generated. Please check the PDF component and try again.",
//         confirmButtonColor: "#f97316",
//       });
//     } finally {
//       setPdfLoading(false);
//     }
//   };

//   /*
//   |--------------------------------------------------------------------------
//   | Table columns
//   |--------------------------------------------------------------------------
//   */

//   const columns = [
//     {
//       title: "ID",
//       dataIndex: "id",
//       key: "id",
//       width: 70,
//       responsive: ["md"],
//     },
//     {
//       title: "Student",
//       dataIndex: "student_name",
//       key: "student_name",
//       width: 180,
//       render: (value) => <Text strong>{value || "-"}</Text>,
//     },
//     {
//       title: "Fee Type",
//       dataIndex: "fee_type",
//       key: "fee_type",
//       width: 160,
//       ellipsis: true,
//       render: (value) => value || "-",
//     },
//     {
//       title: "Category",
//       dataIndex: "payment_category",
//       key: "payment_category",
//       width: 130,
//       render: (value) => (
//         <Tag
//           style={{
//             background: value === "School Fee" ? "#84cc16" : "#f97316",
//             color: "#111827",
//             border: "none",
//             fontWeight: 700,
//             borderRadius: 20,
//           }}
//         >
//           {value || "Uncategorized"}
//         </Tag>
//       ),
//     },
//     {
//       title: "Amount",
//       dataIndex: "amount_paid",
//       key: "amount_paid",
//       width: 120,
//       render: (value) => (
//         <Text strong style={{ color: "#f97316" }}>
//           ${Number(value || 0).toLocaleString("en-US", {
//             minimumFractionDigits: 2,
//             maximumFractionDigits: 2,
//           })}
//         </Text>
//       ),
//     },
//     {
//       title: "Balance",
//       dataIndex: "balance",
//       key: "balance",
//       width: 120,
//       render: (value) =>
//         `$${Number(value || 0).toLocaleString("en-US", {
//           minimumFractionDigits: 2,
//           maximumFractionDigits: 2,
//         })}`,
//     },
//     {
//       title: "Payer",
//       dataIndex: "payer_name",
//       key: "payer_name",
//       width: 150,
//       ellipsis: true,
//       responsive: ["lg"],
//       render: (value) => value || "-",
//     },
//     {
//       title: "Cashier",
//       dataIndex: "created_by",
//       key: "created_by",
//       width: 130,
//       responsive: ["lg"],
//       render: (value) => value || "-",
//     },
//     {
//       title: "Date",
//       dataIndex: "created_at",
//       key: "created_at",
//       width: 130,
//       render: (value) => {
//         if (!value) return "-";
//         const date = new Date(value);
//         return Number.isNaN(date.getTime())
//           ? String(value)
//           : date.toLocaleDateString();
//       },
//     },
//     {
//       title: "Year",
//       dataIndex: "academic_year",
//       key: "academic_year",
//       width: 120,
//       responsive: ["xl"],
//       render: (value) =>
//         typeof value === "object"
//           ? value?.name || value?.year || "-"
//           : value || "-",
//     },
//   ];

//   /*
//   |--------------------------------------------------------------------------
//   | Mobile transaction card
//   |--------------------------------------------------------------------------
//   */

//   const MobileTransactionCard = ({ item }) => (
//     <Card className="mobile-payment-card" key={item.id}>
//       <Row gutter={[8, 8]}>
//         <Col span={24}>
//           <Text strong>{item.student_name || "Unnamed Student"}</Text>
//         </Col>

//         <Col span={12}>
//           <Text type="secondary">Fee</Text>
//           <br />
//           {item.fee_type || "-"}
//         </Col>

//         <Col span={12}>
//           <Text type="secondary">Amount</Text>
//           <br />
//           <Text strong style={{ color: "#f97316" }}>
//             ${Number(item.amount_paid || 0).toLocaleString("en-US", {
//               minimumFractionDigits: 2,
//               maximumFractionDigits: 2,
//             })}
//           </Text>
//         </Col>

//         <Col span={12}>
//           <Text type="secondary">Category</Text>
//           <br />
//           <Tag>{item.payment_category || "Uncategorized"}</Tag>
//         </Col>

//         <Col span={12}>
//           <Text type="secondary">Date</Text>
//           <br />
//           {item.created_at
//             ? new Date(item.created_at).toLocaleDateString()
//             : "-"}
//         </Col>

//         <Col span={24}>
//           <Text type="secondary">Cashier</Text>
//           <br />
//           {item.created_by || "-"}
//         </Col>
//       </Row>
//     </Card>
//   );

//   /*
//   |--------------------------------------------------------------------------
//   | Render page
//   |--------------------------------------------------------------------------
//   */

//   return (
//     <div className="daily-payment-wrapper">
//       <Card className="daily-payment-card" bordered={false}>
//         <Row
//           justify="space-between"
//           align="middle"
//           gutter={[16, 16]}
//         >
//           <Col xs={24} md={16}>
//             <Title
//               level={3}
//               style={{ margin: 0, color: "#ffffff" }}
//             >
//               Daily Payment Transactions
//             </Title>

//             <Text style={{ color: "#d1d5db" }}>
//               Student payment history and daily financial records
//             </Text>
//           </Col>

//           <Col xs={24} md={8}>
//             <Tag className="active-year-tag">
//               Active Year: {selectedYear?.name || "No Year"}
//             </Tag>
//           </Col>
//         </Row>

//         <Divider className="dark-divider" />

//         <Row gutter={[12, 12]} align="middle">
//           <Col xs={24} md={8}>
//             <Input
//               size="large"
//               placeholder="Search payment..."
//               prefix={<SearchOutlined />}
//               value={searchText}
//               allowClear
//               onChange={(event) => handleSearch(event.target.value)}
//             />
//           </Col>

//           <Col xs={24} md={5}>
//             <Button
//               block
//               size="large"
//               icon={<ReloadOutlined />}
//               onClick={fetchTransactions}
//               loading={loading}
//               className="refresh-payment-btn"
//             >
//               Refresh
//             </Button>
//           </Col>

//           <Col xs={24} md={5}>
//             <Button
//               block
//               size="large"
//               icon={<FilePdfOutlined />}
//               onClick={handleDownloadPDF}
//               loading={pdfLoading}
//               disabled={transactions.length === 0 || loading}
//               className="daily-pdf-btn"
//             >
//               {pdfLoading ? "Generating PDF..." : "Daily PDF"}
//             </Button>
//           </Col>
//         </Row>

//         <Divider className="dark-divider" />

//         <Row gutter={[12, 12]} align="middle">
//           <Col xs={24} md={12}>
//             <AutoComplete
//               style={{ width: "100%" }}
//               value={studentSearch}
//               options={students.map((student) => ({
//                 key: String(student.id),
//                 value: student.full_name,
//               }))}
//               onSearch={fetchPaidStudents}
//               onSelect={handleStudentSelect}
//               placeholder="Search student payment history..."
//               allowClear
//               onClear={clearStudentSelection}
//             >
//               <Input
//                 size="large"
//                 prefix={<UserOutlined />}
//                 placeholder="Search student payment history..."
//               />
//             </AutoComplete>
//           </Col>

//           {selectedStudent && (
//             <Col xs={24} md={12}>
//               <Tag color="blue">
//                 Selected student: {selectedStudent.full_name}
//               </Tag>

//               <Button
//                 type="link"
//                 onClick={clearStudentSelection}
//               >
//                 Clear selection
//               </Button>
//             </Col>
//           )}
//         </Row>

//         {selectedStudent && (
//           <>
//             <Divider className="dark-divider" />

//             <Title level={5} style={{ color: "#ffffff" }}>
//               Payment History — {selectedStudent.full_name}
//             </Title>

//             {studentTransactions.length === 0 ? (
//               <Empty description="No payment history found for this student." />
//             ) : (
//               <Table
//                 rowKey={(item) => item.id}
//                 columns={columns.filter(
//                   (column) => column.key !== "year"
//                 )}
//                 dataSource={studentTransactions}
//                 pagination={{ pageSize: 10, showSizeChanger: true }}
//                 scroll={{ x: 900 }}
//                 tableLayout="fixed"
//               />
//             )}
//           </>
//         )}

//         <Divider className="dark-divider" />

//         {loading ? (
//           <div className="payment-loading">
//             <Spin size="large" />
//           </div>
//         ) : transactions.length === 0 ? (
//           <div className="payment-empty">
//             <Empty description="No payment records found" />
//           </div>
//         ) : (
//           <>
//             <div className="desktop-payment-table">
//               <Table
//                 rowKey={(item) => item.id}
//                 columns={columns}
//                 dataSource={transactions}
//                 pagination={{
//                   current: pagination.current,
//                   pageSize: pagination.pageSize,
//                   total: totalRecords,
//                   showSizeChanger: true,
//                   pageSizeOptions: [20, 50, 100],
//                   showTotal: (total) => `Total ${total} transactions`,
//                 }}
//                 onChange={handleTableChange}
//                 tableLayout="fixed"
//                 scroll={{ x: 1100 }}
//               />
//             </div>

//             <div className="mobile-payment-list">
//               {transactions.map((item) => (
//                 <MobileTransactionCard
//                   key={item.id}
//                   item={item}
//                 />
//               ))}
//             </div>
//           </>
//         )}
//       </Card>
//     </div>
//   );
// };

// export default DailyPaymentTransactionUpdate;